import {AddSchema, CreateAccessor, GenerateUUID, MGLClass} from "mobx-graphlink";
import {GetValues_ForSchema} from "js-vextensions";
import {GetWordsInText_Array, GetWordsInText_Map} from "../../../Utils/Search/StringToWords.js";
import {MergeSets} from "../../../Utils/General/General.js";

export enum SleepPosition {
	"" = null as any, // looking back: what is this for?
	Back = 10,
	Front = 20,
	LeftSide = 30,
	RightSide = 40,
}

@MGLClass({table: "journalEntries"})
export class JournalEntry {
	constructor(initialData?: Partial<JournalEntry>) {
		this.Extend({
			title: "",
			notes: "",
			segments: [new JournalSegment()],
		});
		this.Extend(initialData);
	}
	id: string;
	creator: string;
	createdAt: number;

	title: string;
	tags: string[];
	sleepTime: number|n;
	wakeTime: number|n;
	wakePosition: SleepPosition;
	notes: string;
	segments: JournalSegment[];
}
AddSchema("JournalEntry", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},

		title: {type: "string"},
		tags: {items: {type: "string"}},
		sleepTime: {type: ["null", "number"]},
		wakeTime: {type: ["null", "number"]},
		wakePosition: {oneOf: GetValues_ForSchema(SleepPosition)},
		notes: {type: "string"},
		segments: {items: {$ref: "JournalSegment"}},
	},
});

//export const EntitySequence_RowSplitter_ID = "<EntitySequence_RowSplitter_ID>";
export class JournalSegment {
	constructor(data?: Partial<JournalSegment>) {
		//this.Extend({shortText: "", longText: ""});
		//this.Extend(initialData);
		Object.assign(this, data);
	}

	id: string|n = GenerateUUID();
	lucid: boolean;
	semiLucid: boolean;
	ordered: boolean;
	length: number; // in seconds
	wakeTime: number|n; // if this is non-null, then this "segment" is just a "wake marker"; should be displayed in UI as just a line or something
	clarity: number|n; // 0-1
	excludeFromIntegration?: boolean;
	excludeFromIntegration_reason?: string|n;

	/** The quality of the techniques performed just before falling asleep, before this dream segment. [range: 0-1]
	 * (to describe tech-exec quality of initial driftoff, make sure to assign this to the first dream segment) */
	techExecution_preSleep?: number|n;
	mappingProgress?: number|n; // 0-1; how much progress has been made on mapping this dream's contents onto the dream-world map

	anchorEntity: string|n; // deprecated
	anchorEntities: string[]|n; // array needed for cases where a dream-segment has multiple titles/"named scenes"
	shortText = "";
	longText = "";
	/** The dream report as actually posted publicly (eg. to a lucid-dreaming Discord server), in the
	 * bulleted markdown form described in `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4.
	 *
	 * This is deliberately SEPARATE from `longText`: Echo Hunt operates only on text the user was
	 * comfortable sharing publicly, and the public posts are also more standardized in form. The
	 * format is:
	 *  - `# <group>` lines ("# First lucid", "# Second lucid", ...) group the night into dreams;
	 *  - `### <section title>` lines head each scene within a group;
	 *  - `* ` lines are the bullets (indented/tab-prefixed children belong to their parent bullet);
	 *  - everything else (prose paragraphs) is context, not a bullet.
	 * See `Source/EchoHunt/Bullets.ts` for the parser. */
	publicText = "";
}
AddSchema("JournalSegment", {
	properties: {
		id: {type: ["string", "null"]},
		lucid: {type: "boolean"},
		semiLucid: {type: "boolean"},
		ordered: {type: "boolean"},
		length: {type: "number"},
		wakeTime: {type: ["null", "number"]},
		clarity: {type: "number"},
		excludeFromIntegration: {type: "boolean"},
		excludeFromIntegration_reason: {type: "boolean"},

		techExecution_preSleep: {type: ["null", "number"]},
		mappingProgress: {type: ["null", "number"]}, // 0-1; how much progress has been made on mapping this dream's contents onto the dream-world map

		anchorEntity: {type: ["null", "string"]},
		anchorEntities: {items: {type: "string"}},
		shortText: {type: "string"},
		longText: {type: "string"},
		publicText: {type: "string"},
	},
});

export const JournalSegment_HideableField_values = [
	...[
		"semiLucid", "ordered", "length", "clarity",
		"excludeFromIntegration", "excludeFromIntegration_reason", "techExecution_preSleep", "mappingProgress",
		"id",
	] as Array<keyof JournalSegment>,
	// these are for UI controls that don't map neatly to JournalSegment fields, but still need visibility toggling
	...[
		"llmTools",
		"textTransforms",
	]
];
export type JournalSegment_HideableField = typeof JournalSegment_HideableField_values[number];

export type WordsSource = "shortText" | "longText" | "both";
export const GetWordsInDreamSegment_Array = CreateAccessor((segment: JournalSegment, source: WordsSource, includeWordShards: boolean, dedup: boolean, lowercase = true)=>{
	const terms = [
		...(source.IsOneOf("shortText", "both") ? GetWordsInText_Array(segment.shortText, includeWordShards, lowercase) : []),
		...(source.IsOneOf("longText", "both") ? GetWordsInText_Array(segment.longText, includeWordShards, lowercase) : []),
	];
	return dedup ? terms.Distinct() : terms;
});
export const GetWordsInDreamSegment_Map = CreateAccessor((segment: JournalSegment, source: WordsSource, includeWordShards: boolean, lowercase = true)=>{
	return MergeSets(
		source.IsOneOf("shortText", "both") && GetWordsInText_Map(segment.shortText, includeWordShards, lowercase),
		source.IsOneOf("longText", "both") && GetWordsInText_Map(segment.longText, includeWordShards, lowercase),
	);
});
export const GetWordsInDreamEntry_Map = CreateAccessor((entry: JournalEntry, source: WordsSource, includeWordShards: boolean, lowercase = true)=>{
	return MergeSets(...entry.segments.map(segment=>GetWordsInDreamSegment_Map(segment, source, includeWordShards, lowercase)));
});

export function GetJournalEntryAllText(entry: JournalEntry, lowercase = false) {
	let result = `${entry.title}\n${entry.segments.map(a=>GetSegmentAllText(a)).join("\n")}`;
	if (lowercase) result = result.toLowerCase();
	return result;
}
export function GetSegmentAllText(segment: JournalSegment, lowercase = false) {
	let result = `${segment.shortText}\n${segment.longText}`;
	if (lowercase) result = result.toLowerCase();
	return result;
}